import { Suspense, lazy } from "react";
import { Route } from "react-router-dom";
import { ProtectedRoute, PublicAuthRedirectRoute, PublicAuthRoute, RouteFallback } from "./RouteGuards";

const Index = lazy(() => import("@/pages/Index"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const LesseesPage = lazy(() => import("@/pages/LesseesPage"));
const LessorsPage = lazy(() => import("@/pages/LessorsPage"));
const OemsPage = lazy(() => import("@/pages/OemsPage"));
const BrowsePage = lazy(() => import("@/pages/BrowsePage"));
const EquipmentDetailPage = lazy(() => import("@/pages/EquipmentDetailPage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const ServiceDetailPage = lazy(() => import("@/pages/ServiceDetailPage"));
const ServiceProvidersLandingPage = lazy(() => import("@/pages/ServiceProvidersLandingPage"));
const ExploreRequestsPage = lazy(() => import("@/pages/ExploreRequestsPage"));
const PostRequestPage = lazy(() => import("@/pages/PostRequestPage"));
const StakeholderProfilePage = lazy(() => import("@/pages/StakeholderProfilePage"));
const BnplPage = lazy(() => import("@/pages/BnplPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage"));
const ListItemPage = lazy(() => import("@/pages/ListItemPage"));
const AgentLandingPage = lazy(() => import("@/pages/AgentLandingPage"));
const SearchResultsPage = lazy(() => import("@/pages/SearchResultsPage"));
const CategoriesPage = lazy(() => import("@/pages/CategoriesPage"));
const AdvertisePage = lazy(() => import("@/pages/AdvertisePage"));
const AboutUsPage = lazy(() => import("@/pages/AboutUsPage"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const HelpCenterPage = lazy(() => import("@/pages/HelpCenterPage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const SafetyTipsPage = lazy(() => import("@/pages/SafetyTipsPage"));
const TermsOfUsePage = lazy(() => import("@/pages/TermsOfUsePage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));

const withSuspense = (element: JSX.Element, title?: string) => (
  <Suspense fallback={<RouteFallback title={title} />}>
    {element}
  </Suspense>
);

export const PublicRoutes = () => (
  <>
    <Route path="/" element={withSuspense(<Index />, "Loading homepage...")} />
    <Route path="/for-lessees" element={withSuspense(<LesseesPage />)} />
    <Route path="/for-lessors" element={withSuspense(<LessorsPage />)} />
    <Route path="/for-oems" element={withSuspense(<OemsPage />)} />
    <Route path="/service-providers" element={withSuspense(<ServiceProvidersLandingPage />)} />
    <Route path="/agents" element={withSuspense(<AgentLandingPage />)} />
    <Route path="/browse" element={withSuspense(<BrowsePage />)} />
    <Route path="/search" element={withSuspense(<SearchResultsPage />)} />
    <Route path="/requests" element={withSuspense(<ExploreRequestsPage />)} />
    <Route path="/requests/post" element={<ProtectedRoute>{withSuspense(<PostRequestPage />)}</ProtectedRoute>} />
    <Route path="/partners/:type/:id" element={withSuspense(<StakeholderProfilePage />)} />
    <Route path="/categories" element={withSuspense(<CategoriesPage />)} />
    <Route path="/equipment/:id" element={withSuspense(<EquipmentDetailPage />)} />
    <Route path="/services/:id" element={withSuspense(<ServiceDetailPage />)} />
    <Route path="/services" element={withSuspense(<ServicesPage />)} />
    <Route path="/about" element={withSuspense(<AboutUsPage />)} />
    <Route path="/careers" element={withSuspense(<CareersPage />)} />
    <Route path="/blog/:postId" element={withSuspense(<BlogPostPage />)} />
    <Route path="/blog" element={withSuspense(<BlogPage />)} />
    <Route path="/pricing" element={withSuspense(<PricingPage />)} />
    <Route path="/help-center" element={withSuspense(<HelpCenterPage />)} />
    <Route path="/faq" element={withSuspense(<HelpCenterPage />)} />
    <Route path="/support" element={withSuspense(<SupportPage />)} />
    <Route path="/forgot-password" element={<PublicAuthRoute>{withSuspense(<ForgotPasswordPage />)}</PublicAuthRoute>} />
    <Route path="/reset-password" element={<PublicAuthRoute>{withSuspense(<ResetPasswordPage />)}</PublicAuthRoute>} />
    <Route path="/safety-tips" element={withSuspense(<SafetyTipsPage />)} />
    <Route path="/terms" element={withSuspense(<TermsOfUsePage />)} />
    <Route path="/privacy" element={withSuspense(<PrivacyPolicyPage />)} />
    <Route path="/bnpl" element={withSuspense(<BnplPage />)} />
    <Route path="/how-it-works" element={withSuspense(<HowItWorksPage />)} />
    <Route path="/advertise" element={withSuspense(<AdvertisePage />)} />
    <Route path="/login" element={<PublicAuthRoute>{withSuspense(<LoginPage />)}</PublicAuthRoute>} />
    <Route path="/register" element={<PublicAuthRedirectRoute to="/login?mode=register" />} />
    <Route path="/list-item" element={<ProtectedRoute>{withSuspense(<ListItemPage />)}</ProtectedRoute>} />
    <Route path="*" element={withSuspense(<NotFound />)} />
  </>
);
